import {
  CalendarOutlined,
  FileTextOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useMediaQuery } from "@mui/material";
import { Col, DatePicker, Input, Row } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CREATE_jwel } from "../Config/Config";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";
import AdvanceDetails from "./AdvanceDetailes";
import CommodityWisePurchases from "./CounterWiseSales";
import LatestDues from "./LatestDues";
import PaymentOverview from "./PaymentOverview";
import SalesmanCommissions from "./PendingOrders";
import PurchasesTable from "./ProductWiseSalesTable";
import SupplierDueAlerts from "./Supplierduealerts";
import TodaysSalesBarGraph from "./TodaysSalesBarGraph";
import TodaysSalesPieChart from "./TodaysSalesPieChart";
import TopTenStats from "./Toptenstats";

// QuickAccessWidget mounts once in DashboardLayout.jsx so it shows on every
// screen — it's intentionally not imported or rendered here.

const INVOICE_API =
  "https://retailtextiles.timeserasoftware.in/api/ERP/GetDataFromGivenTableNamewithwhere";
const INVOICE_TABLE = "CREDIT_BILLMAST";
const REFRESH_MS = 5 * 60 * 1000;
const INVOICE_MIN_WIDTH = 640; // list scrolls sideways below this width

const { RangePicker } = DatePicker;

const lowerKeys = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));

const accent = { bg: "#f5f0ff", border: "#b8a4e8", text: "#4c3494" };

/* ------------------------------------------------------------------ */
/* Invoice list header                                                 */
/* ------------------------------------------------------------------ */
const headStyle = {
  fontSize: 10,
  fontWeight: 700,
  color: "#7c5cbf",
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const ColHeader = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "6px 12px 6px 10px",
      borderRadius: 8,
      background: "linear-gradient(90deg,#f5f0ff,#ede5ff55)",
      margin: "0 0 6px",
    }}
  >
    <div style={{ width: 36, flexShrink: 0 }} />
    <div style={{ ...headStyle, minWidth: 76, flexShrink: 0 }}>Invoice</div>
    <div style={{ ...headStyle, minWidth: 90, flexShrink: 0 }}>Type</div>
    <div style={{ ...headStyle, flex: 1 }}>Customer</div>
    <div style={{ ...headStyle, minWidth: 36, textAlign: "center" }}>Pcs</div>
    <div style={{ ...headStyle, minWidth: 80, textAlign: "right" }}>
      Total / Disc
    </div>
    <div style={{ ...headStyle, minWidth: 90, textAlign: "right" }}>
      Net Amt
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Invoice row                                                         */
/* ------------------------------------------------------------------ */
const BillCard = ({ record, index }) => {
  const b = lowerKeys(record);
  const custName = b.custname && b.custname !== "-" ? b.custname : "Walk-in";
  const phone = b.phoneno && b.phoneno !== "-" ? b.phoneno : "";
  const billType = b.billtype || b.ctype || "—";

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: Math.min(index * 0.035, 0.5),
        duration: 0.32,
        ease: "easeOut",
      }}
      whileHover={{ y: -2, boxShadow: `0 6px 20px ${accent.border}35` }}
      style={{
        background: "#fff",
        borderRadius: 10,
        padding: "7px 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        boxShadow: "0 1px 6px rgba(44,27,105,0.06)",
        border: `1px solid ${accent.border}33`,
        cursor: "default",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          flexShrink: 0,
          background: accent.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${accent.border}44`,
        }}
      >
        <span style={{ fontSize: 15 }}>🧾</span>
      </div>

      <div style={{ minWidth: 76, flexShrink: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: accent.text,
            lineHeight: 1.2,
          }}
        >
          #{b.billno}
        </div>
        <div
          style={{ fontSize: 9, color: "#bbb", marginTop: 1, fontWeight: 500 }}
        >
          {moment(b.billdate).format("DD MMM YY")}
        </div>
      </div>

      <div style={{ minWidth: 90, flexShrink: 0 }}>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            padding: "2px 7px",
            borderRadius: 20,
            background: accent.bg,
            color: accent.text,
            border: `1px solid ${accent.border}55`,
            whiteSpace: "nowrap",
          }}
        >
          {billType}
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#2d1b69",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {custName}
        </div>
        <div style={{ fontSize: 9, color: "#bbb", marginTop: 1 }}>{phone}</div>
      </div>

      <div style={{ textAlign: "center", flexShrink: 0, minWidth: 36 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#4c3494" }}>
          {b.totqty}
        </div>
      </div>

      <div
        style={{
          flexShrink: 0,
          background: "#f8f5ff",
          borderRadius: 9,
          padding: "6px 10px",
          textAlign: "center",
          minWidth: 80,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6,
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 600, color: "#6366f1" }}>
            T
          </span>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#4338ca" }}>
            {Number(b.totamt || 0).toFixed(2)}
          </span>
        </div>
        <div style={{ borderTop: "1px solid #ede8ff", margin: "3px 0" }} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6,
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 600, color: "#f59e0b" }}>
            D
          </span>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#b45309" }}>
            {Number(b.discount || 0).toFixed(2)}
          </span>
        </div>
      </div>

      <div style={{ textAlign: "right", flexShrink: 0, minWidth: 90 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#43aa8b" }}>
          ₹
          {Number(b.netamt || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}
        </div>
      </div>
    </motion.div>
  );
};

/* ------------------------------------------------------------------ */
/* Motion variants                                                     */
/* ------------------------------------------------------------------ */
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 250, damping: 25 },
  },
};

/* ------------------------------------------------------------------ */
/* Dashboard                                                           */
/* ------------------------------------------------------------------ */
const Dashboard = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const isSmall = useMediaQuery("(max-width:480px)");

  const [open, setOpen] = useState(false);
  const userArea = localStorage.getItem("city");
  const userName = localStorage.getItem("user");
  const singleImage = localStorage.getItem("singleImage");
  const tenantName = localStorage.getItem("tenantName");

  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [searchBillNo, setSearchBillNo] = useState("");
  const [originalData, setOriginalData] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  /* ---- Carousel images ------------------------------------------- */
  useEffect(() => {
    const fetchFirmConfig = async () => {
      try {
        const response = await axios.get(
          `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=FIRM_CONFIGURE`,
          { headers: { tenantName } },
        );
        const data = response.data;
        const firmConfig = Array.isArray(data) ? data[0] : data;

        if (firmConfig?.EPASS1) {
          setCarouselImages(
            firmConfig.EPASS1.split(",")
              .map((url) => url.trim())
              .filter(Boolean),
          );
        }
      } catch (error) {
        console.error("Error fetching firm config:", error);
      }
    };
    fetchFirmConfig();
  }, [tenantName]);

  useEffect(() => {
    if (carouselImages.length <= 1) return undefined;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % carouselImages.length);
    }, 1500);
    return () => clearInterval(interval);
  }, [carouselImages]);

  /* ---- Invoices -------------------------------------------------- */
  const fetchData = useCallback(async () => {
    try {
      const startDate = (dateRange?.[0] || dayjs()).format("MM/DD/YYYY");
      const endDate = (dateRange?.[1] || dayjs()).format("MM/DD/YYYY");

      const response = await axios.get(
        `${INVOICE_API}?tableName=${INVOICE_TABLE}&where=BILLDATE>='${startDate}' AND BILLDATE<='${endDate}'`,
        { headers: { tenantName } },
      );

      const rows = Array.isArray(response.data) ? response.data : [];
      const data = rows
        .map((item) => {
          const n = lowerKeys(item);
          return {
            ...item,
            BillNo: n.billno,
            BillDate: moment(n.billdate).format("YYYY-MM-DD"),
          };
        })
        .sort((a, b) => Number(b.BillNo) - Number(a.BillNo)); // newest first
      setOriginalData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setOriginalData([]);
    }
  }, [dateRange, tenantName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const intervalId = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  // Search filter derived from the fetched data
  const tableData = useMemo(() => {
    const q = searchBillNo.trim().toLowerCase();
    if (!q) return originalData;
    return originalData.filter((item) =>
      item.BillNo?.toString().toLowerCase().includes(q),
    );
  }, [originalData, searchBillNo]);

  const handleRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    } else {
      setDateRange([dayjs(), dayjs()]);
    }
  };

  const toggleDrawer = () => setOpen(false);

  const bottomCards = [
    <SalesmanCommissions key="a" />,
    <PurchasesTable key="b" />,
    <SupplierDueAlerts key="c" />,
  ];

  const sectionPad = isMobile ? "0 6px" : "0 10px";

  return (
    <div style={{ maxWidth: "100vw", overflowX: "hidden" }}>
      <Header setOpen={setOpen} />
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        style={{
          backgroundColor: "#f5f3ff",
          minHeight: "100vh",
          padding: "4px 0 16px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background orbs */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: -100,
            left: -100,
            width: 400,
            height: 400,
            background:
              "radial-gradient(circle, rgba(124, 92, 191, 0.15) 0%, rgba(245, 243, 255, 0) 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <motion.div
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.2, 0.5, 0.2],
            x: [0, -60, 0],
            y: [0, 40, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: -150,
            right: -50,
            width: 500,
            height: 500,
            background:
              "radial-gradient(circle, rgba(184, 164, 232, 0.15) 0%, rgba(245, 243, 255, 0) 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <div style={{ position: "relative", zIndex: 1, padding: sectionPad }}>
          {/* ---------------- Carousel ---------------- */}
          {carouselImages.length > 0 && (
            <div className="carouselContainer">
              <div
                className="carouselSlider"
                style={{
                  width: `${carouselImages.length * 100}%`,
                  transform: `translateX(-${
                    currentIndex * (100 / carouselImages.length)
                  }%)`,
                }}
              >
                {carouselImages.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`img-${index}`}
                    className="carouselImage"
                    style={{ width: `${100 / carouselImages.length}%` }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ---------------- Row 1: summary + dues ---------------- */}
          <Row gutter={[14, 14]}>
            <Col xs={24} lg={16}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -4 }}
                style={{
                  background: "#fff",
                  borderRadius: 14,
                  padding: "12px 14px",
                  boxShadow: "0 4px 20px rgba(44,27,105,0.09)",
                  height: "100%",
                  transition: "box-shadow 0.3s ease",
                  overflowX: "auto",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#2d1b69",
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 3,
                      height: 14,
                      borderRadius: 3,
                      background: "linear-gradient(180deg,#2d1b69,#b8a4e8)",
                    }}
                  />
                  Today's Summary
                </div>
                <AdvanceDetails />
              </motion.div>
            </Col>

            <Col xs={24} lg={8}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -4 }}
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  transition: "all 0.3s ease",
                }}
              >
                <LatestDues />
              </motion.div>
            </Col>
          </Row>

          {/* ---------------- Row 2: charts ---------------- */}
          <Row gutter={[14, 14]} style={{ marginTop: 14 }}>
            <Col xs={24} sm={12} lg={6}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -4 }}
                style={{ height: "100%", transition: "all 0.3s ease" }}
              >
                <TodaysSalesPieChart />
              </motion.div>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -4 }}
                style={{ height: "100%" }}
              >
                <TopTenStats />
              </motion.div>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -4 }}
                style={{ height: "100%", transition: "all 0.3s ease" }}
              >
                <CommodityWisePurchases />
              </motion.div>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -4 }}
                style={{
                  background: "#fff",
                  borderRadius: 14,
                  padding: "12px 12px 10px",
                  boxShadow: "0 4px 20px rgba(44,27,105,0.10)",
                  height: isMobile ? "auto" : 280,
                  minHeight: 280,
                  display: "flex",
                  flexDirection: "column",
                  transition: "box-shadow 0.3s ease",
                }}
              >
                <PaymentOverview />
              </motion.div>
            </Col>
          </Row>

          {/* ---------------- Row 3: invoices + bar graph ---------------- */}
          <Row gutter={[14, 14]} style={{ marginTop: 14 }}>
            <Col xs={24} lg={15}>
              <motion.div
                variants={itemVariants}
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  overflow: "hidden",
                  boxShadow: "0 4px 20px rgba(44,27,105,0.09)",
                }}
              >
                {/* Gradient header */}
                <div
                  style={{
                    padding: "9px 14px",
                    background:
                      "linear-gradient(90deg,#2d1b69 0%,#4c3494 60%,#7c5cbf 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 8,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      right: -20,
                      top: -20,
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.07)",
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: -10,
                      bottom: -20,
                      width: 55,
                      height: 55,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.04)",
                      pointerEvents: "none",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      zIndex: 1,
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 9,
                        background: "rgba(255,255,255,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <FileTextOutlined
                        style={{ fontSize: 15, color: "#fff" }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: "#fff",
                          lineHeight: 1.2,
                        }}
                      >
                        Latest Invoices
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "rgba(255,255,255,0.65)",
                        }}
                      >
                        {tableData.length} bill
                        {tableData.length !== 1 ? "s" : ""} found
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 8,
                      zIndex: 1,
                      width: isSmall ? "100%" : "auto",
                    }}
                  >
                    <div className="inv-rp">
                      <RangePicker
                        value={dateRange}
                        onChange={handleRangeChange}
                        format="DD/MM/YYYY"
                        allowClear={false}
                        size="small"
                        suffixIcon={
                          <CalendarOutlined
                            style={{
                              color: "rgba(255,255,255,0.7)",
                              fontSize: 10,
                            }}
                          />
                        }
                        popupStyle={{ zIndex: 9999 }}
                        disabledDate={(c) => c && c.isAfter(dayjs(), "day")}
                      />
                    </div>

                    <div className="inv-search">
                      <Input
                        placeholder="Bill No"
                        size="small"
                        style={{ width: "100%", maxWidth: 140 }}
                        onChange={(e) => setSearchBillNo(e.target.value)}
                        allowClear
                        value={searchBillNo}
                        prefix={<SearchOutlined style={{ fontSize: 10 }} />}
                      />
                    </div>
                  </div>
                </div>

                {/* Header + list scroll sideways together on narrow screens */}
                <div style={{ overflowX: "auto", paddingTop: 6 }}>
                  <div style={{ minWidth: INVOICE_MIN_WIDTH }}>
                    <div style={{ padding: "0 14px" }}>
                      <ColHeader />
                    </div>
                    <div
                      style={{
                        height: isMobile ? 320 : 200,
                        overflowY: "auto",
                        padding: "6px 14px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                      }}
                    >
                      <AnimatePresence>
                        {tableData.length === 0 ? (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            style={{
                              textAlign: "center",
                              padding: "48px 0",
                              color: "#b8a4e8",
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            <div style={{ fontSize: 36, marginBottom: 8 }}>
                              📋
                            </div>
                            No invoices found for the selected range
                          </motion.div>
                        ) : (
                          tableData.map((record, i) => (
                            <BillCard
                              key={`${record.BillNo}-${i}`}
                              record={record}
                              index={i}
                            />
                          ))
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </motion.div>
            </Col>

            <Col xs={24} lg={9}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -4 }}
                style={{ height: "100%", transition: "all 0.3s ease" }}
              >
                <TodaysSalesBarGraph />
              </motion.div>
            </Col>
          </Row>

          {/* ---------------- Row 4: bottom tables ---------------- */}
          <Row gutter={[14, 14]} style={{ marginTop: 14 }} align="top">
            {bottomCards.map((component, idx) => (
              <Col key={idx} xs={24} md={12} xl={8}>
                <motion.div
                  variants={itemVariants}
                  whileHover={isMobile ? undefined : { scale: 1.01 }}
                  style={{
                    height: isMobile ? "auto" : 400,
                    minHeight: 360,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(44,27,105,0.08)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {component}
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </div>

        <style>{`
          ::-webkit-scrollbar { width: 4px; height: 4px; }
          ::-webkit-scrollbar-track { background: #f5f0ff; border-radius: 4px; }
          ::-webkit-scrollbar-thumb { background: #b8a4e8; border-radius: 4px; }

          /* Carousel */
          .carouselContainer {
            width: 100%;
            overflow: hidden;
            height: 130px;
            background: #f0f0f0;
            border-radius: 10px;
            margin-bottom: 16px;
          }
          .carouselSlider {
            display: flex;
            height: 100%;
            transition: transform 0.3s ease-in-out;
          }
          .carouselImage {
            flex: 0 0 auto;
            height: 100%;
            object-fit: cover;
            border-radius: 10px;
            display: block;
          }
          @media (min-width: 768px) {
            .carouselContainer { height: 180px; }
          }
          @media (min-width: 1025px) {
            .carouselContainer { height: 220px; }
          }

          /* Invoice header controls */
          .inv-rp .ant-picker {
            background: rgba(255,255,255,0.14) !important;
            border: 1px solid rgba(255,255,255,0.30) !important;
            border-radius: 20px !important;
            padding: 2px 10px !important;
            height: 26px !important;
            box-shadow: none !important;
          }
          .inv-rp .ant-picker:hover,
          .inv-rp .ant-picker-focused {
            border-color: rgba(255,255,255,0.55) !important;
            box-shadow: none !important;
          }
          .inv-rp .ant-picker-input > input {
            color: #fff !important;
            font-size: 10px !important;
            font-weight: 700 !important;
            width: 70px !important;
          }
          .inv-rp .ant-picker-input > input::placeholder { color: rgba(255,255,255,0.5) !important; }
          .inv-rp .ant-picker-separator,
          .inv-rp .ant-picker-suffix { color: rgba(255,255,255,0.6) !important; font-size: 10px !important; }
          .inv-rp .ant-picker-clear { display: none !important; }

          .inv-search .ant-input-affix-wrapper {
            background: rgba(255,255,255,0.14) !important;
            border: 1px solid rgba(255,255,255,0.30) !important;
            border-radius: 20px !important;
            padding: 2px 10px !important;
            height: 26px !important;
            box-shadow: none !important;
          }
          .inv-search .ant-input-affix-wrapper:hover,
          .inv-search .ant-input-affix-wrapper-focused {
            border-color: rgba(255,255,255,0.55) !important;
            box-shadow: none !important;
          }
          .inv-search .ant-input {
            background: transparent !important;
            color: #fff !important;
            font-size: 10px !important;
            font-weight: 600 !important;
          }
          .inv-search .ant-input::placeholder { color: rgba(255,255,255,0.5) !important; }
          .inv-search .ant-input-prefix { color: rgba(255,255,255,0.6) !important; }
          .inv-search .ant-input-clear-icon { color: rgba(255,255,255,0.6) !important; }

          @media (max-width: 480px) {
            .inv-rp, .inv-search { width: 100%; }
            .inv-rp .ant-picker { width: 100% !important; }
            .inv-search .ant-input-affix-wrapper { width: 100% !important; }
          }
        `}</style>
      </motion.div>

      <SidebarDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        singleImage={singleImage}
        userArea={userArea}
        userName={userName}
      />
    </div>
  );
};

export default Dashboard;
